import React, { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useRouter } from "next/router";
import Page from "components/ui/Pages/Page";
import styles from "./Pages.module.scss";
import Pagination from "./Pagination";
import LoadingAnimation from "../loaders/LoadingAnimation/LoadingAnimation";

export default function Pages({ children, onNav }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageDisabled, setNextPageDisabled] = useState(true);

  const router = useRouter();
  const { pathname, query, isReady } = router;
  const { page } = query;
  const queryPageNumber = page ? parseInt(page) : 1;

  const { formState, watch, trigger } = useFormContext();
  const { errors } = formState;
  const formValues = watch();

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if ((isLoaded && isReady) || !queryPageNumber) return;

    setIsLoaded(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPageNumber, setIsLoaded, isReady]);

  const handlePageChange = (pageNumber, doTrigger = true) => {
    if (!router.isReady) {
      return;
    }

    setCurrentPage(pageNumber);

    if (router.isReady) {
      router
        .replace(
          {
            pathname: pathname,
            query: { ...router.query, page: pageNumber },
          },
          undefined,
          {
            shallow: true,
            scroll: false,
          }
        )
        .catch((event) => {
          if (!event.cancelled) {
            throw event;
          }
        });
    }

    !!window && window.dataLayer && dataLayer
      ? dataLayer?.push({
          event: "form_page_change",
          form: "travel_guide",
          page: pageNumber,
        })
      : null;

    onNav(pageNumber, doTrigger);
  };

  useEffect(() => {
    if (
      !isLoaded ||
      !router.isReady ||
      !!router.query.travel_guide_form_values
    ) {
      return;
    }

    if (router.query.page && queryPageNumber === currentPage) {
      return;
    }

    if (
      (errors?.location || !formValues?.location) &&
      !!queryPageNumber &&
      queryPageNumber != 1
    ) {
      // Bounce to page 1 if location is missing
      handlePageChange(1, false);
    } else {
      // If the page number in state and the one passed in query param are different, update the state to match query param

      handlePageChange(queryPageNumber, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, router.isReady, router.query.travel_guide_form_values]);

  const paginatedChildren = children.map((child, index) => {
    return (
      <Page key={index} pageNumber={index + 1} currentPage={currentPage}>
        {child}
      </Page>
    );
  });

  useEffect(() => {
    if (!isLoaded || !router?.query?.page) {
      return;
    }
    // Page validation needs to update based on a lot of different potential changes
    const inputsByPage = {};

    for (let [index, childPage] of children.entries()) {
      if (!childPage?.props?.page?.inputs) {
        inputsByPage[index] = [];
        continue;
      }

      inputsByPage[index] = childPage.props.page.inputs.map(
        (input) => input?.props?.id ?? null
      );
    }

    if (!Object.entries(inputsByPage).length) return;

    const currentPageHasError = inputsByPage[Math.abs(currentPage - 1)].some(
      (inputId) => {
        if (!errors[inputId]) return false;
        return true;
      }
    );

    if (currentPageHasError) {
      setNextPageDisabled(true);
      return;
    }

    setNextPageDisabled(false);
  }, [
    children,
    currentPage,
    errors,
    formState,
    page,
    formValues,
    isLoaded,
    router.query,
  ]);

  return (
    <div className={`${styles.container}`}>
      {!isLoaded && (
        <div className={`${styles.loading}`}>
          <LoadingAnimation />
        </div>
      )}
      {isLoaded && (
        <>
          <div className={`${styles.pages}`}>{paginatedChildren}</div>
          <div className={`${styles.paginationWrapper}`}>
            <Pagination
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              pageCount={paginatedChildren.length}
              nextPageDisabled={nextPageDisabled}
            />
          </div>
        </>
      )}
    </div>
  );
}
